<script setup>
/**
 * Renders a page's blocks on the admin builder canvas using the EXACT public-site
 * components (frontend/components/blocks/*) and the shared grouping
 * (frontend/utils/blockGroups.js). This is the ONLY block render path now — there
 * is no admin-side copy of block markup, so the preview is byte-identical to the
 * published page and can't drift.
 *
 * Selection wiring: a top-level block carries `_i` (its index in the page's blocks
 * array), set by LiveCanvas. Each such leaf is wrapped with a `data-block-index`
 * hook the builder scans for (click-to-select + freeform inline editing). When
 * rendered nested — e.g. as the global <BlockRenderer> that TwoColumnBlock recurses
 * through — the blocks have no `_i`, so no hooks/affordances are added and the
 * markup stays identical to the site.
 */
import HeroBlock         from "~/components/blocks/HeroBlock.vue";
import RichTextBlock     from "~/components/blocks/RichTextBlock.vue";
import GalleryBlock      from "~/components/blocks/GalleryBlock.vue";
import EventListBlock    from "~/components/blocks/EventListBlock.vue";
import MediaArticleBlock from "~/components/blocks/MediaArticleBlock.vue";
import MediaListBlock    from "~/components/blocks/MediaListBlock.vue";
import DocumentListBlock from "~/components/blocks/DocumentListBlock.vue";
import CTABlock          from "~/components/blocks/CTABlock.vue";
import ContactBlock      from "~/components/blocks/ContactBlock.vue";
import TwoColumnBlock    from "~/components/blocks/TwoColumnBlock.vue";
import EmbedBlock        from "~/components/blocks/EmbedBlock.vue";
import FreeformBlock     from "~/components/blocks/FreeformBlock.vue";
import UnsupportedBlock  from "~/components/builder/UnsupportedBlock.vue";
import { groupBlocks, groupByEvent } from "~/utils/blockGroups";
import { useI18n } from "@/preview/nuxtShims";

defineProps({
  blocks: { type: Array, required: true },
  // Index of the selected top-level block (for the green selection outline).
  selectedIndex: { default: null },
});
const emit = defineEmits(["select"]);

const { t } = useI18n();

// Per-type component for "single" runs (everything but grouped article/contact).
const SINGLE_MAP = {
  hero: HeroBlock,
  rich_text: RichTextBlock,
  gallery: GalleryBlock,
  event_list: EventListBlock,
  cta: CTABlock,
  contact: ContactBlock,
  two_column: TwoColumnBlock,
  embed: EmbedBlock,
  media_list: MediaListBlock,
  document_list: DocumentListBlock,
  freeform: FreeformBlock,
};

// Click a top-level block → select it, unless the click landed inside an
// inline-editable region (which must keep focus for typing).
function onClick(b, e) {
  if (b?._i == null) return;
  if (e.target.closest('[contenteditable="true"]')) return;
  emit("select", b._i);
}
</script>

<template>
  <template v-for="(group, gi) in groupBlocks(blocks)" :key="gi">
    <!-- Media articles: grouped by event, 3-up grid (mirrors BlockRenderer) -->
    <section v-if="group.kind === 'articles'" class="section-soft">
      <div class="container-x space-y-12">
        <div v-for="(sub, si) in groupByEvent(group.items)" :key="si">
          <div v-if="sub.event" class="mb-6 flex items-center gap-3">
            <span class="h-[3px] w-10 shrink-0 rounded-full bg-rainbow-line" aria-hidden="true" />
            <h2 class="font-display text-xl font-bold leading-snug text-ink md:text-2xl">{{ sub.event }}</h2>
          </div>
          <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="(b, i) in sub.items" :key="i"
              class="hk-cell"
              :class="{ 'is-selected': b._i != null && selectedIndex === b._i }"
              :data-block-index="b._i ?? null"
              @click="onClick(b, $event)"
            >
              <MediaArticleBlock :data="b.data" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Office/contact cards: one section, count-aware grid -->
    <section v-else-if="group.kind === 'contacts'" class="section">
      <div class="container-x">
        <div class="mb-8 flex items-center gap-3">
          <span class="h-[3px] w-12 rounded-full bg-rainbow-line" aria-hidden="true" />
          <span class="text-sm font-semibold uppercase tracking-[0.2em] text-brand-green">{{ t("footer.offices") }}</span>
        </div>
        <div
          class="grid gap-6"
          :class="group.items.length === 1 ? 'max-w-md'
                : group.items.length === 2 ? 'sm:grid-cols-2'
                : 'sm:grid-cols-2 lg:grid-cols-3'"
        >
          <div
            v-for="(b, i) in group.items" :key="i"
            class="hk-cell"
            :class="{ 'is-selected': b._i != null && selectedIndex === b._i }"
            :data-block-index="b._i ?? null"
            @click="onClick(b, $event)"
          >
            <ContactBlock :data="b.data" />
          </div>
        </div>
      </div>
    </section>

    <!-- Single blocks: dispatch by type, each wrapped for select/hover/index -->
    <template v-else>
      <div
        v-for="(b, i) in group.items" :key="`${gi}-${i}`"
        class="hk-block"
        :class="{ 'is-selected': b._i != null && selectedIndex === b._i }"
        :data-block-index="b._i ?? null"
        @click="onClick(b, $event)"
      >
        <component :is="SINGLE_MAP[b.type]" v-if="SINGLE_MAP[b.type]" :data="b.data" />
        <UnsupportedBlock v-else :type="b.type" />
      </div>
    </template>
  </template>
</template>
